import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@brent/core-ui/ui'

const RETRY_KEY = 'google_auth_retry'

export default function AuthCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { handleCallback, login } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const processedRef = useRef(false)

  useEffect(() => {
    if (processedRef.current) return
    processedRef.current = true

    const errorParam = searchParams.get('error_description') || searchParams.get('error')

    if (errorParam) {
      // Check if the error is from account linking (first-time Google sign-in)
      if (errorParam.includes('ACCOUNT_LINKED')) {
        const hasRetried = sessionStorage.getItem(RETRY_KEY)
        if (!hasRetried) {
          // Auto-retry: the account has been linked, signing in again should succeed
          sessionStorage.setItem(RETRY_KEY, 'true')
          login()
          return
        }
        // Already retried once - show error
        sessionStorage.removeItem(RETRY_KEY)
        setError('Account linking completed but sign-in failed. Please try again.')
        return
      }

      if (errorParam.includes('ACCESS_DENIED')) {
        sessionStorage.removeItem(RETRY_KEY)
        setError('Your email is not registered. Please contact an administrator to get access.')
        return
      }

      sessionStorage.removeItem(RETRY_KEY)
      setError(errorParam)
      return
    }

    // Exchange code for tokens (handleCallback reads code/state from URL)
    sessionStorage.removeItem(RETRY_KEY)
    handleCallback()
      .then(() => {
        navigate('/suggestions', { replace: true })
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Sign in failed. Please try again.')
      })
  }, [searchParams, handleCallback, login, navigate])

  if (error) {
    return (
      <div className="container max-w-md mx-auto py-12">
        <Card>
          <CardHeader>
            <CardTitle>Sign In Failed</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Button variant="outline" className="w-full" onClick={() => navigate('/auth')}>
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center space-y-4">
        <LoadingSpinner />
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  )
}
